import { Box, Chip, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { toast } from "react-toastify";

export interface PayoutDetail {
  id: number;
  payment_method_type: string;
  payment_method_label: string;
  account_holder_name: string;
  account_number: string;
  bank_name?: string | null;
  is_default: boolean;
}

interface Props {
  details: PayoutDetail[];
}

/** Payout details the organiser submitted, shown in full to approvers only
 * (the API returns `payout_details` for approvers and null otherwise). */
const PayoutDetailsCard = ({ details }: Props) => {
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Account number copied.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
      <Typography fontWeight={700} sx={{ mb: 1.5 }}>
        Payout details
      </Typography>
      {details.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          The organiser has not submitted any payout details.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {details.map((d) => (
            <Box
              key={d.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Chip
                size="small"
                label={d.payment_method_label}
                color={d.is_default ? "primary" : "default"}
                variant="outlined"
              />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography fontWeight={600}>{d.account_holder_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {d.account_number}
                  {d.bank_name ? ` · ${d.bank_name}` : ""}
                </Typography>
              </Box>
              <Tooltip title="Copy account number">
                <IconButton size="small" onClick={() => copy(d.account_number)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default PayoutDetailsCard;
